import { useEffect, useState } from "react";
import api from "@/lib/api";
import { RefreshCw } from "lucide-react";

const GIORNI_SETT = ["LUN", "MAR", "MER", "GIO", "VEN"];
const NUM_TO_GIORNO = { 0: "LUN", 1: "MAR", 2: "MER", 3: "GIO", 4: "VEN" };
const lbl = { fontSize: 10, fontWeight: 700, color: "#666", textTransform: "uppercase", letterSpacing: 0.3 };
const inp = { width: "100%", padding: "6px 8px", border: "1px solid #bbb", borderRadius: 3, fontSize: 12, boxSizing: "border-box" };
function btnSm(bg) {
  return { background: bg, color: "white", border: "none", padding: "3px 8px", fontSize: 10, fontWeight: 700, cursor: "pointer", borderRadius: 2 };
}

function DayToggleRow({ giorni, onToggle, colorOn }) {
  return (
    <div style={{ display: "flex", gap: 4 }}>
      {GIORNI_SETT.map((g, i) => {
        const on = giorni.has(i);
        return (
          <button key={i} type="button" onClick={() => onToggle(i)}
            style={{ minWidth: 40, padding: "5px 6px", fontSize: 11, fontWeight: 800, cursor: "pointer",
                     background: on ? colorOn : "#eceff1", color: on ? "white" : "#90a4ae",
                     border: on ? `1px solid ${colorOn}` : "1px solid #cfd8dc", borderRadius: 3 }}>
            {g}
          </button>
        );
      })}
    </div>
  );
}

export default function CambioCorsoModal({ isc, onClose, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [dataCambio, setDataCambio] = useState(isc.data_recesso || today);
  const [giorniNuovi, setGiorniNuovi] = useState(new Set((isc.giorni || []).map(Number)));
  const [note, setNote] = useState("");
  const [nuove, setNuove] = useState([]);
  const [corsi, setCorsi] = useState([]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/mensa-matrice/corsi", { params: { anno: isc.anno } });
        setCorsi(r.data?.items || r.data?.corsi || []);
      } catch { setCorsi([]); }
    })();
  }, [isc.anno]);

  const toggleGiorno = (i) => {
    const s = new Set(giorniNuovi);
    s.has(i) ? s.delete(i) : s.add(i);
    setGiorniNuovi(s);
  };
  const addNuova = () => setNuove([...nuove, { corso_codice: "", giorni: new Set(), tariffa_etichetta: "", importo: "", credito: "" }]);
  const updNuova = (idx, patch) => setNuove(nuove.map((n, i) => i === idx ? { ...n, ...patch } : n));
  const delNuova = (idx) => setNuove(nuove.filter((_, i) => i !== idx));
  const toggleNuovaGiorno = (idx, g) => {
    const n = nuove[idx]; const s = new Set(n.giorni);
    s.has(g) ? s.delete(g) : s.add(g);
    updNuova(idx, { giorni: s });
  };

  const salva = async () => {
    setSaving(true); setErr(null);
    try {
      const body = {
        data_cambio: dataCambio || "",
        giorni_nuovi: Array.from(giorniNuovi).sort((a, b) => a - b),
        note,
        nuove_iscrizioni: nuove.filter(n => n.corso_codice).map(n => ({
          corso_codice: n.corso_codice,
          giorni: Array.from(n.giorni).sort((a, b) => a - b),
          tariffa_etichetta: n.tariffa_etichetta || "",
          importo: Number(n.importo || 0),
          credito: Number(n.credito || 0),
        })),
      };
      await api.post(`/mensa-matrice/extra-alunni/iscrizione/${isc._id}/cambio-corso`, body);
      onSaved();
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore salvataggio cambio corso");
    } finally { setSaving(false); }
  };

  const giorniOrig = (isc.giorni || []).map(n => NUM_TO_GIORNO[Number(n)]).join(", ") || "—";
  const importoAttuale = isc.tariffa_importo != null ? isc.tariffa_importo : isc.prezzo;

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 6000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()} data-testid="cambio-corso-modal"
           style={{ background: "white", width: "100%", maxWidth: 680, maxHeight: "92vh", overflow: "auto", borderRadius: 6 }}>
        <div style={{ background: "linear-gradient(90deg,#4a148c,#6a1b9a)", color: "white", padding: "12px 16px", display: "flex", justifyContent: "space-between", alignItems: "center", position: "sticky", top: 0 }}>
          <div style={{ fontWeight: 800, fontSize: 14, display: "inline-flex", alignItems: "center", gap: 6 }}><RefreshCw size={16} strokeWidth={2.5} /> Cambio corso · {isc.corso_codice}</div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "white", fontSize: 22, cursor: "pointer" }}>✕</button>
        </div>
        <div style={{ padding: 16, display: "grid", gap: 12 }}>
          <div style={{ background: "#f3e5f5", border: "1px solid #ce93d8", borderRadius: 4, padding: 10, fontSize: 12 }}>
            <b>{isc.corso_descrizione}</b> — importo attuale {importoAttuale != null ? `€ ${Number(importoAttuale).toLocaleString("it-IT", { minimumFractionDigits: 2 })}` : "—"} · giorni attuali: <b>{giorniOrig}</b>
            <div style={{ color: "#6a1b9a", marginTop: 4 }}>L'iscrizione resta <b>pagata</b> con l'importo attuale (nessun rimborso). Qui aggiorni i giorni e, se serve, aggiungi nuovi corsi.</div>
          </div>

          <label style={lbl}>DATA CAMBIO (la mensa userà i nuovi giorni da questa data)</label>
          <input type="date" value={dataCambio} onChange={(e) => setDataCambio(e.target.value)} data-testid="cambio-data" style={inp} />

          <label style={lbl}>NUOVI GIORNI del corso attuale (es. tieni solo GIO)</label>
          <DayToggleRow giorni={giorniNuovi} onToggle={toggleGiorno} colorOn="#2e7d32" />
          {giorniNuovi.size === 0 && (
            <div data-testid="cambio-recesso-hint" style={{ fontSize: 11, fontWeight: 700, color: "#c62828", background: "#ffebee", border: "1px solid #ef9a9a", borderRadius: 3, padding: "6px 8px" }}>
              ⊘ Nessun giorno selezionato → l'iscrizione <b>{isc.corso_codice}</b> verrà marcata come <b>receduta</b> il {dataCambio || "—"}. Aggiungi sotto il nuovo corso sostitutivo, se serve.
            </div>
          )}

          <label style={lbl}>NOTA VARIAZIONE (es. "da 2gg MAR+GIO a 1gg GIO")</label>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} data-testid="cambio-note" style={{ ...inp, fontFamily: "inherit", resize: "vertical" }} />

          <div style={{ borderTop: "1px dashed #ccc", paddingTop: 10 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
              <label style={lbl}>NUOVE ISCRIZIONI (da pagare · nessun addebito automatico)</label>
              <button type="button" onClick={addNuova} data-testid="cambio-add-corso"
                      style={{ padding: "4px 10px", background: "#00695c", color: "white", border: "none", fontSize: 11, fontWeight: 700, cursor: "pointer", borderRadius: 3 }}>+ Aggiungi corso</button>
            </div>
            {nuove.map((n, idx) => (
              <div key={idx} data-testid={`cambio-nuova-${idx}`} style={{ border: "1px solid #b2dfdb", background: "#e0f2f1", borderRadius: 4, padding: 8, marginBottom: 8, display: "grid", gap: 6 }}>
                <select value={n.corso_codice} onChange={(e) => updNuova(idx, { corso_codice: e.target.value })}
                        data-testid={`cambio-nuova-corso-${idx}`} style={inp}>
                  <option value="">— Seleziona corso —</option>
                  {corsi.map(c => <option key={c.codice} value={c.codice}>{c.codice} · {c.descrizione}</option>)}
                </select>
                <DayToggleRow giorni={n.giorni} onToggle={(g) => toggleNuovaGiorno(idx, g)} colorOn="#00695c" />
                <div style={{ display: "flex", gap: 6 }}>
                  <input value={n.tariffa_etichetta} onChange={(e) => updNuova(idx, { tariffa_etichetta: e.target.value })} placeholder="Etichetta tariffa" style={{ ...inp, flex: 2 }} />
                  <input value={n.importo} onChange={(e) => updNuova(idx, { importo: e.target.value })} type="number" step="0.01" placeholder="Importo €" data-testid={`cambio-nuova-importo-${idx}`} style={{ ...inp, flex: 1 }} />
                  <input value={n.credito} onChange={(e) => updNuova(idx, { credito: e.target.value })} type="number" step="0.01" placeholder="Credito da scalare €" style={{ ...inp, flex: 1 }} />
                  <button type="button" onClick={() => delNuova(idx)} style={{ ...btnSm("#c62828"), padding: "0 10px" }}>🗑</button>
                </div>
                {(Number(n.importo || 0) - Number(n.credito || 0)) !== Number(n.importo || 0) && (
                  <div style={{ fontSize: 11, color: "#00695c", fontWeight: 700 }}>Netto da pagare: € {(Number(n.importo || 0) - Number(n.credito || 0)).toLocaleString("it-IT", { minimumFractionDigits: 2 })}</div>
                )}
              </div>
            ))}
          </div>

          {err && <div style={{ background: "#ffebee", color: "#b71c1c", padding: 8, fontSize: 11, border: "1px solid #ef9a9a", borderRadius: 3 }}>{err}</div>}
        </div>
        <div style={{ borderTop: "1px solid #eee", padding: "10px 16px", display: "flex", justifyContent: "flex-end", gap: 8, position: "sticky", bottom: 0, background: "white" }}>
          <button onClick={onClose} style={{ padding: "6px 14px", background: "white", border: "1px solid #ccc", fontSize: 12, fontWeight: 700, cursor: "pointer", borderRadius: 2 }}>Annulla</button>
          <button onClick={salva} disabled={saving} data-testid="cambio-save"
                  style={{ padding: "6px 16px", background: "#6a1b9a", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: saving ? "wait" : "pointer", borderRadius: 2 }}>
            {saving ? "Salvataggio…" : "💾 Salva cambio corso"}
          </button>
        </div>
      </div>
    </div>
  );
}
